import React from 'react';
import styled from '@emotion/styled';

import { Container, Col, Row } from '@/components/grid';
import Error from '@/pages/_error';
import { SPACE, CACHE } from '@/constants';
import { H1 } from '@/components/type';
import Layout from '@/components/layout';
import TextLinksList from '@/components/text-links-list';
import LinkList from '@/components/link-list';
import { generateSitemapLinks } from '@/utils/sitemap/generate-sitemap-links';
import { withAppProps, withAppProviders } from '@/utils/withAppProps';
import { getGlobalSchema, getGlobalSeo } from '@tac/sanity-client/queries/global/seo';
import type { Seo, SitemapQueryResult } from '@tac/sanity-client/types';
import { getGlobalStructuredData } from '@/utils/seo/structured-data';
import { getVehicleSearchFilters } from '@/utils/apollo-requests/vehicles/get-vehicles';
import { parseVehicleMakes, type VehicleMakeEntry } from '@/utils/sitemap/parse-vehicle-makes';
import { getSitemap } from '@tac/sanity-client/queries/hsiyc/page';

const StyledH1 = styled(H1)`
  margin-bottom: ${
    // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'hasExtraSpacing' does not exist on type '{ theme?: Theme | undefined; as?: ElementType<any, keyof IntrinsicElements> | undefined; } & ClassAttributes<HTMLHeadingElement> & HTMLAttributes<...> & { ...; } & { ...; }'.
    (props) => (props.hasExtraSpacing ? SPACE.X3 : `calc(${SPACE.X1} + ${SPACE.X0})`)
  };
  margin-top: ${SPACE.X3};
`;

type SitemapProps = {
  sanityPages: SitemapQueryResult;
  vehicleMakesEntries: VehicleMakeEntry[];
  statusCode?: number;
  seo?: Seo;
};

const Sitemap = ({ statusCode = 200, sanityPages, vehicleMakesEntries, seo }: SitemapProps) => {
  const titleRef = React.useRef(null);

  React.useEffect(() => {
    if (titleRef.current) {
      // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'focus' does not exist on type 'never'.
      titleRef.current.focus();
    }
  }, [titleRef]);

  if (statusCode !== 200) {
    return <Error {...{ statusCode }} />;
  }

  const sitemapLinks = generateSitemapLinks({ sanityPages, vehicleMakesEntries });

  return (
    <Layout seo={seo}>
      <Container maxWidth="xl">
        <StyledH1 tabIndex={-1} ref={titleRef}>
          Site Map
        </StyledH1>

        <Row>
          <Col width={{ xs: 12, lg: 6 }}>
            <h2>Pages</h2>
            <TextLinksList>
              <LinkList items={sitemapLinks.sanityPages} />
            </TextLinksList>
          </Col>

          <Col width={{ xs: 12, lg: 6 }}>
            <h2>Vehicle Makes</h2>
            <TextLinksList>
              <LinkList items={sitemapLinks.vehicleMakesEntries} />
            </TextLinksList>
          </Col>
        </Row>
      </Container>
    </Layout>
  );
};

const getStaticProps = withAppProps(async () => {
  const [vehicleFilters, sanityPages, globalSeo, globalSchema] = await Promise.all([
    getVehicleSearchFilters(),
    getSitemap(),
    getGlobalSeo('hsiyc'),
    getGlobalSchema('hsiyc'),
  ]);

  const vehicleMakesEntries = parseVehicleMakes(vehicleFilters);

  const structuredData = getGlobalStructuredData(globalSeo, globalSchema);
  const seo = Object.assign({}, globalSeo?.seo, { structuredData });

  return {
    props: {
      trackingMeta: {
        content_type: 'informational',
        content_group: 'sitemap',
      },
      sanityPages,
      vehicleMakesEntries,
      seo,
    },
    revalidate: CACHE.REVALIDATE,
  };
});

export default withAppProviders(Sitemap);
export { getStaticProps };
